/** Selector del tenant operativo validado por el servidor. */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useMemberships, useSwitchContext } from '@/services/api/useAuth';
import { extractErrorMessage } from '@/services/api/client';
import { tenantApi } from '@/services/api/tenantApi';

export function TenantContextSelector({ required = false }: { required?: boolean }) {
  const { user } = useAuth();
  const canOperateTenants = user?.permissions.includes('platform.tenants.operate') ?? false;
  const canListPlatformTenants = user?.permissions.includes('platform.tenants.view') ?? false;
  const { data, isLoading, isError } = useMemberships();
  const { data: platformTenants = [], isLoading: isLoadingPlatform, isError: isPlatformError } = useQuery({
    queryKey: ['auth', 'platform-tenants'],
    queryFn: () => tenantApi.list(),
    enabled: canOperateTenants && canListPlatformTenants,
  });
  const { mutate: switchContext, isPending, error } = useSwitchContext();
  const memberships = data ?? user?.memberships ?? [];
  const activeTenantId = user?.active_tenant_id ?? null;
  const [selectedId, setSelectedId] = useState('');
  const choice = selectedId || activeTenantId || '';
  // La selección global requiere `platform.tenants.operate`; sin ese permiso
  // solo se ofrecen los tenants de la identidad, no existe bypass por bandera.
  const activeOptions: { optionKey: string; tenant_id: string; tenant_name: string }[] = canOperateTenants
    ? platformTenants
        .filter((tenant) => tenant.is_active)
        .map((tenant) => ({ optionKey: `platform:${tenant.id}`, tenant_id: tenant.id, tenant_name: tenant.name }))
    : memberships
        .filter((item) => item.status === 'active')
        .map((item) => ({ optionKey: `membership:${item.membership_id}`, tenant_id: item.tenant_id, tenant_name: item.tenant_name }));

  return (
    <div className={required ? 'mx-auto max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-sm' : 'flex items-center gap-2'}>
      {required && (
        <>
          <h1 className="text-xl font-semibold text-slate-900">Selecciona un tenant</h1>
          <p className="mt-1 mb-4 text-sm text-slate-600">Elige el espacio de trabajo para continuar.</p>
        </>
      )}
      <label htmlFor={required ? 'tenant-context-required' : 'tenant-context-header'} className={required ? 'block text-sm font-medium text-slate-700' : 'sr-only'}>
        Tenant activo
      </label>
      <select
        id={required ? 'tenant-context-required' : 'tenant-context-header'}
        value={choice}
        onChange={(event) => setSelectedId(event.target.value)}
        disabled={isLoading || isLoadingPlatform || isPending}
        className="min-w-[150px] max-w-[220px] rounded-md border border-slate-300 bg-white px-2 py-2 text-sm text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <option value="">Selecciona un tenant</option>
        {activeOptions.map((item) => (
          <option key={item.optionKey} value={item.tenant_id}>{item.tenant_name}</option>
        ))}
      </select>
      <button
        type="button"
        onClick={() => choice && switchContext(choice)}
        disabled={!choice || choice === activeTenantId || isPending}
        className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
      >
        {required ? 'Continuar' : 'Cambiar'}
      </button>
      {(isError || isPlatformError || error) && <p role="alert" className="text-sm text-error">{error ? extractErrorMessage(error) : 'No se pudieron cargar los tenants.'}</p>}
      {required && !isLoading && !isLoadingPlatform && activeOptions.length === 0 && <p className="mt-3 text-sm text-slate-600">No hay tenants disponibles para esta cuenta.</p>}
    </div>
  );
}
